import React, { useMemo } from 'react';
import { Box, Chip } from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { beautifyNumber } from '../../Utils/FormatNumber.js';
import './SummaryBar.css';
import { useClientNavigation } from '../../hooks/useClientNavigation.js';

const SummaryChip = ({ label, value, extraClass, beautifyNumberOrNot = true, onClick }) => (
    <Chip
        label={`${label}: ${beautifyNumberOrNot ? beautifyNumber(value) : value}`}
        className={`summary-chip ${extraClass}`}
        onClick={onClick ? () => onClick() : undefined}
        style={onClick ? { cursor: 'pointer' } : undefined}
    />
);

const SummaryStatCard = ({ label, value, extraClass, icon, onClick }) => (
    <button
        type="button"
        className={`summary-stat-card ${extraClass}`}
        onClick={onClick || undefined}
        disabled={!onClick}
    >
        <div className="summary-stat-text">
            <span className="summary-stat-value">{value}</span>
            <span className="summary-stat-label">{label}</span>
        </div>
        <span className="summary-stat-icon">{icon}</span>
    </button>
);

const SummaryBar = ({ clients, t, variant = 'chips' }) => {
    const {
        viewAssignments,
    } = useClientNavigation();

    const navigateToAssignments = () => {
        viewAssignments();
    };

    const totals = useMemo(() => {
        const sum = (arr, fn) =>
            arr.reduce((acc, cur) => acc - (- fn(cur)), 0);

        const reportingClients = (clients || []).filter(c => !c.isManageOnly);

        return {
            balance: sum(clients || [], c => typeof c.balance === 'number' ? c.balance : 0),
            docs: sum(reportingClients, c => c.totalCounts || 0),
            missions: sum(clients || [], c => c.unfinishedAssignments || 0),
        };
    }, [clients]);

    if (variant === 'cards') {
        return (
            <Box className="summary-bar summary-bar-cards">
                <SummaryStatCard
                    extraClass="balance"
                    label={t('totalBalance')}
                    value={`₪ ${beautifyNumber(totals.balance)}`}
                    icon={<AccountBalanceWalletOutlinedIcon />}
                />
                <SummaryStatCard
                    extraClass="docs"
                    label={t('unprocessedDocuments')}
                    value={beautifyNumber(totals.docs, 0, true)}
                    icon={<DescriptionOutlinedIcon />}
                />
                <SummaryStatCard
                    extraClass="missions"
                    label={t('pendingMissions')}
                    value={beautifyNumber(totals.missions, 0, true)}
                    icon={<CalendarMonthOutlinedIcon />}
                    onClick={navigateToAssignments}
                />
            </Box>
        );
    }

    return (
        <Box className="summary-bar">
            <SummaryChip label={t('totalBalance')} value={totals.balance} extraClass="balance" />
            <SummaryChip label={t('unprocessedDocuments')} value={totals.docs} extraClass="docs" beautifyNumberOrNot={false} />
            <SummaryChip
                label={t('pendingMissions')}
                value={totals.missions}
                extraClass="missions"
                beautifyNumberOrNot={false}
                onClick={() => navigateToAssignments()}
            />
        </Box>
    );
};

export default SummaryBar;
